import { PostAuthComponentParams } from '../../types/auth';
import { useEffect, useState } from 'react';
import {
  Button,
  Container,
  ContentLayout,
  Form,
  Header,
  SpaceBetween,
  TextContent,
} from '@amzn/awsui-components-react';
import { useIntl } from 'react-intl';
import signInSwitchNeededMessages from '../../i18n/signInSwitchNeeded.messages';
import { useAmplifyConfig } from '../../hooks/useAmplifyConfig';
import { replayAuth } from '../../services/auth';
import { emitPostAuthRumEvent } from '../../services/emitPostAuthRumEvent';
import { PostAuthRumEvent } from '../../constants/metrics';
import { isStandardProvider } from '../../constants/providers';

enum SwitchType {
  B2B = 'b2b',
  B2I = 'b2i',
}

interface SwitchContext {
  idpToUse: string;
  type: SwitchType;
}

const SignInSwitchNeeded = ({
  user,
  onContinue,
  onCancel,
  config,
}: PostAuthComponentParams) => {
  const [signInSwitchFlowEnabled, setEnableSignInSwitch] =
    useState<boolean>(false);
  const { formatMessage } = useIntl();

  useAmplifyConfig(config);

  const getSwitchContext = (): SwitchContext | null => {
    if (user.signin_switch_needed_b2i) {
      return { idpToUse: user.signin_switch_needed_b2i, type: SwitchType.B2I };
    }
    if (user.signin_switch_needed) {
      return { idpToUse: user.signin_switch_needed, type: SwitchType.B2B };
    }
    return null;
  };

  const switchContext = getSwitchContext();

  useEffect(() => {
    if (config.enableSignInSwitch === 'true' && switchContext) {
      setEnableSignInSwitch(true);
      emitPostAuthRumEvent(user, PostAuthRumEvent.SIGN_IN_SWITCH_NEEDED);
    } else {
      onContinue();
    }
  }, [config.enableSignInSwitch, switchContext, onContinue, user]);

  const handleContinueToIdp = () => {
    replayAuth(
      switchContext?.idpToUse!,
      new URLSearchParams(window.location.search)
    );
  };

  const getContainerBodyMessage = () => {
    const messageKey =
      switchContext?.type === SwitchType.B2I
        ? signInSwitchNeededMessages.containerBodyB2I
        : signInSwitchNeededMessages.containerBody;

    return formatMessage(messageKey, {
      idpToUse: getIdpFriendlyName(),
    });
  };

  const getIdpFriendlyName = () => {
    if (!switchContext?.idpToUse) return '';

    return isStandardProvider(switchContext.idpToUse)
      ? switchContext.idpToUse
      : formatMessage(signInSwitchNeededMessages.singleSignOn);
  };

  if (!signInSwitchFlowEnabled || !switchContext) {
    return <></>;
  }

  const idpFriendlyName = getIdpFriendlyName();

  return (
    <ContentLayout
      header={
        <Header variant={'h1'}>
          {formatMessage(signInSwitchNeededMessages.header, {
            idpToUse: idpFriendlyName,
          })}
        </Header>
      }
    >
      <Container
        header={
          <Header variant={'h2'}>
            {formatMessage(signInSwitchNeededMessages.containerTitle)}
          </Header>
        }
      >
        <Form
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  onCancel();
                }}
                data-testid={'return-to-signin'}
              >
                {formatMessage(
                  signInSwitchNeededMessages.returnToSignInPageButton
                )}
              </Button>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  handleContinueToIdp();
                }}
                data-testid={'continue-to-signin'}
                variant={'primary'}
              >
                {formatMessage(signInSwitchNeededMessages.continueButton, {
                  idpToUse: idpFriendlyName,
                })}
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween direction="vertical" size="xs">
            <TextContent>{getContainerBodyMessage()}</TextContent>
          </SpaceBetween>
        </Form>
      </Container>
    </ContentLayout>
  );
};

export default SignInSwitchNeeded;
